import { useCallback, useEffect } from "react";
import type { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import { TableFilter } from "~enums/tableFilter.ts";
import type { DataTableFilterDefinition } from "~types/dataTableFilterDefinition.ts";
import { loadFromLocalStorage } from "~utilities/loadFromLocalStorage.ts";
import { saveToLocalStorage } from "~utilities/saveToLocalStorage.ts";
import type { DataTableFilter } from "../types/dataTableFilter.ts";

const isFiltersRecord = (value: unknown): value is Record<string, Array<any>> => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  return Object.values(value as Record<string, unknown>).every((val) => Array.isArray(val) || (typeof val === "object" && val !== null));
};

export const useTableFilters = (sportFiltersConfig: DataTableFilterDefinition[], filtersConfig?: FiltersConfig) => {
  const {
    filters: dataTableFilters,
    currentFilter,
    handleFilterChange,
    filtersLocalStorageKey,
  } = filtersConfig ?? {
    filters: [],
  };

  useEffect(() => {
    if (filtersLocalStorageKey && handleFilterChange) {
      const storedFilters = loadFromLocalStorage(filtersLocalStorageKey);
      if (storedFilters) {
        if (isFiltersRecord(storedFilters)) {
          handleFilterChange(storedFilters);
        } else {
          console.warn("Invalid filters format loaded from localStorage");
          handleFilterChange({});
        }
      }
    }
  }, [filtersLocalStorageKey, handleFilterChange]);

  const onFilterChange = useCallback(
    (filterKey: string, values: any) => {
      const newFilters = { ...currentFilter, [filterKey]: values };
      if (handleFilterChange) {
        if (filtersLocalStorageKey) {
          saveToLocalStorage(filtersLocalStorageKey, JSON.stringify(newFilters));
        }
        handleFilterChange(newFilters);
      }
    },
    [handleFilterChange, currentFilter, filtersLocalStorageKey],
  );

  const allFilters = Object.fromEntries(dataTableFilters.map((filter) => [filter.filterKey, filter]));

  const filteredByConfigFilters = sportFiltersConfig?.map((filter) => ({
    ...allFilters[filter.filterKey],
    tableFilterType: filter.tableFilterType || allFilters[filter.filterKey]?.tableFilterType,
  }));

  const primaryFilters = filteredByConfigFilters?.filter(
    (filter) => filter.tableFilterType === TableFilter.Primary,
  ) as Array<DataTableFilter>;
  const secondaryFilters = filteredByConfigFilters?.filter(
    (filter) => filter.tableFilterType === TableFilter.Secondary,
  ) as Array<DataTableFilter>;

  return { primaryFilters, secondaryFilters, onFilterChange };
};
